import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';

/**
 * Cached presigned URL entry
 */
interface CachedUrl {
  url: string;
  expiresAt: Date;
}

/**
 * Response from presigned URL endpoint
 */
interface PresignedUrlResponse {
  event_id: number;
  media_type: 'snapshot' | 'video' | 'thumbnail';
  url: string;
  expires_in: number;
  expires_at: string;
}

/**
 * Service for managing presigned URLs from private S3 bucket
 * 
 * This service:
 * - Caches presigned URLs to reduce API calls
 * - Automatically refreshes expired URLs
 * - Provides type-safe access to event media
 * 
 * Usage:
 * ```ts
 * const url = await s3PresignedUrlService.getEventSnapshot(123);
 * <img src={url} alt="Event snapshot" />
 * ```
 */
class S3PresignedUrlService {
  private cache: Map<string, CachedUrl> = new Map();
  
  // Refresh URLs 5 minutes before expiration
  private readonly REFRESH_BUFFER_MS = 5 * 60 * 1000;

  /**
   * Generate cache key for event media
   */
  private getCacheKey(eventId: number, mediaType: string): string {
    return `event_${eventId}_${mediaType}`;
  }

  /**
   * Check if cached URL is still valid
   */
  private isUrlValid(cached: CachedUrl): boolean {
    const now = new Date();
    const expiresWithBuffer = new Date(cached.expiresAt.getTime() - this.REFRESH_BUFFER_MS);
    return now < expiresWithBuffer;
  }

  /**
   * Get presigned URL from API
   */
  private async fetchPresignedUrl(
    eventId: number,
    mediaType: 'snapshot' | 'video' | 'thumbnail',
    expiration: number = 3600
  ): Promise<string> {
    try {
      const response = await apiClient.get<PresignedUrlResponse>(
        `${API_CONFIG.ENDPOINTS.EVENTS}/${eventId}/presigned-url`,
        { media_type: mediaType, expiration }
      );

      // Cache the URL
      const cacheKey = this.getCacheKey(eventId, mediaType);
      this.cache.set(cacheKey, {
        url: response.url,
        expiresAt: new Date(response.expires_at),
      });

      return response.url;
    } catch (error: any) {
      // If event doesn't have this media type, return empty string
      if (error.response?.status === 404) {
        console.warn(`No ${mediaType} available for event ${eventId}`);
        return '';
      }
      throw error;
    }
  }

  /**
   * Get presigned URL for event media (with caching)
   */
  async getPresignedUrl(
    eventId: number,
    mediaType: 'snapshot' | 'video' | 'thumbnail',
    expiration: number = 3600
  ): Promise<string> {
    const cacheKey = this.getCacheKey(eventId, mediaType);
    const cached = this.cache.get(cacheKey);

    // Return cached URL if still valid
    if (cached && this.isUrlValid(cached)) {
      return cached.url;
    }

    // Fetch new URL if not cached or expired
    return this.fetchPresignedUrl(eventId, mediaType, expiration);
  }

  /**
   * Get presigned URL for event snapshot image
   */
  async getEventSnapshot(eventId: number, expiration: number = 3600): Promise<string> {
    return this.getPresignedUrl(eventId, 'snapshot', expiration);
  }

  /**
   * Get presigned URL for event video
   */
  async getEventVideo(eventId: number, expiration: number = 3600): Promise<string> {
    return this.getPresignedUrl(eventId, 'video', expiration);
  }

  /**
   * Get presigned URL for event thumbnail
   */
  async getEventThumbnail(eventId: number, expiration: number = 3600): Promise<string> {
    return this.getPresignedUrl(eventId, 'thumbnail', expiration);
  }

  /**
   * Preload presigned URLs for multiple events (batch optimization)
   * 
   * Call this when loading a page with multiple events to warm up the cache
   */
  async preloadEventUrls(
    eventIds: number[],
    mediaType: 'snapshot' | 'video' | 'thumbnail' = 'snapshot',
    expiration: number = 3600
  ): Promise<void> {
    const promises = eventIds.map(eventId =>
      this.getPresignedUrl(eventId, mediaType, expiration).catch(err => {
        console.warn(`Failed to preload URL for event ${eventId}:`, err);
        return '';
      })
    );

    await Promise.all(promises);
  }

  /**
   * Clear all cached URLs
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear cached URL for specific event
   */
  clearEventCache(eventId: number): void {
    ['snapshot', 'video', 'thumbnail'].forEach(mediaType => {
      const cacheKey = this.getCacheKey(eventId, mediaType);
      this.cache.delete(cacheKey);
    });
  }

  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats(): { size: number; entries: Array<{ key: string; expiresAt: string; isValid: boolean }> } {
    const entries = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      expiresAt: value.expiresAt.toISOString(),
      isValid: this.isUrlValid(value),
    }));

    return {
      size: this.cache.size,
      entries,
    };
  }
}

// Export singleton instance
export const s3PresignedUrlService = new S3PresignedUrlService();
